import React, { useState, useEffect } from "react";
import VideoCard from "../components/VideoCard";
import { XMarkIcon } from "@heroicons/react/24/outline";
import BackButton from "../components/BackButton";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

function Videos() {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Load videos from Firebase
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const videosRef = collection(db, "videos");
        const q = query(videosRef, orderBy("title"));
        const querySnapshot = await getDocs(q);

        const videosData = [];
        querySnapshot.forEach((doc) => {
          videosData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setVideos(videosData);
        setFilteredVideos(videosData);
        setLoading(false);
      } catch (error) {
        console.error("Error loading videos:", error);
        setError("Failed to load videos");
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Filter videos based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredVideos(videos);
    } else {
      const filtered = videos.filter((video) =>
        video.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVideos(filtered);
    }
  }, [searchTerm, videos]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Videos Section */}
      <div className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <BackButton to="/" />

          {/* Header with Title and Search */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
            <h1 className="text-3xl font-bold">Videos</h1>

            {/* Search Bar */}
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full sm:w-64 px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading videos...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!loading && !error && filteredVideos.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {searchTerm
                  ? "No videos found matching your search."
                  : "No videos found."}
              </p>
            </div>
          )}

          {!loading && !error && filteredVideos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Videos;
